import { useMemo } from 'react';

import { useMutation, UseMutationResult, useQueryClient } from 'react-query';

import { useSnackbar } from 'src/utils/hooks';

import { QUERY_NAME } from '../constants';
import { ContentHubFileSchema, UpdatePageFileVariables } from '../typings';
import { updateContentHubPageFile } from '../requests/updateContentHubPageFile';

interface UseMutationContext {
  previousData?: ContentHubFileSchema[];
}

export type UseUpdateContentHubPageFileResult = UseMutationResult<
  ContentHubFileSchema,
  Error,
  UpdatePageFileVariables,
  UseMutationContext
>;

export const useUpdateContentHubPageFile = (
  pageId: number
): UseUpdateContentHubPageFileResult => {
  const queryClient = useQueryClient();

  const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSnackbar();

  const queryKey = useMemo(() => {
    return pageId
      ? [QUERY_NAME.fetchContentHubPageFiles, pageId]
      : [QUERY_NAME.fetchContentHubPageFiles];
  }, [pageId]);

  const mutation = useMutation<
    ContentHubFileSchema,
    Error,
    UpdatePageFileVariables,
    UseMutationContext
  >((variables) => updateContentHubPageFile(pageId, variables), {
    onMutate: async () => {
      await queryClient.cancelQueries(queryKey);

      const previousData =
        queryClient.getQueryData<ContentHubFileSchema[]>(queryKey);

      return { previousData };
    },

    onSuccess: ({ id, ...restData }) => {
      queryClient.setQueryData<ContentHubFileSchema[]>(
        queryKey,
        (previous = []) =>
          previous.map((file) => {
            if (file.id === id) {
              return { ...file, ...restData };
            }

            return file;
          })
      );

      enqueueSuccessSnackbar({
        message: 'page.content_management.api.files.update',
        intlValues: {
          name: restData.name,
          extension: restData.extension,
        },
      });
    },

    onError: ({ message }, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousData);

      enqueueErrorSnackbar({ message });
    },
  });

  return mutation;
};
